import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Clock, CheckCircle, XCircle, DollarSign,
  MessageCircle, Calendar, ChevronDown, ChevronRight,
  FileText, Star, ExternalLink, AlertCircle,
  Receipt, ChevronLeft, ChevronRight as ChevronRightIcon, Download,
  User, MapPin, Timer, CreditCard, UserCheck, ShoppingCart, Gift
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { fetchQuotesByUserIdWithFilters } from "../../redux/Slices/quoteSlice";
import { PurchaseQuoteTypeEnum, QuoteStatusEnum } from "../../modules/shared/model/quote.enum";
import { Badge } from "./badge";
import { Button } from "./Button";
import QuoteDetailModal from "./QuoteDetailModal";
import { useNavigate } from "react-router-dom";
import QuotePurchaseModal from "./QuotePurchaseModal";
import { useUserCredits } from "../../hooks/useUserCredits";
import { fetchUserCredits } from "../../redux/Slices/creditSlice";
import { useSelector } from "react-redux";
import { GlobalService } from "../../modules";
import { fetchCategoryFormSettings } from "../../redux/Actions/CategoryFormSettingsAction";
import ProfileCompletionDialog from "./ProfileCompletionDialog";
import VerificationRequirementDialog from "../VerificationRequirementDialog";
import BillingAddressRequirementDialog from "./BillingAddressRequirementDialog";
import { useVerification } from "../../hooks/useVerification";
import { checkUserBillingInfo } from "../../utils/billingUtils";
import { RoleEnum } from "../../modules/shared/model";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

interface PurchasedQuoteListingProps {
  userId: string;
  purchaseTypeIds?: string; // Comma-separated purchase type IDs (e.g., "1,2" for Credit and Purchase, or "3" for Assign)
}

const PurchasedQuoteListing: React.FC<PurchasedQuoteListingProps> = ({ userId, purchaseTypeIds }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userQuotes, loading, error, totalCount, hasNextPage } = useAppSelector((state: any) => state.quotes);
  const [selectedQuote, setSelectedQuote] = useState<any>(null);
  const [showQuoteDetails, setShowQuoteDetails] = useState(false);
  const [showQuotePurchaseModal, setShowQuotePurchaseModal] = useState(false);
  const [selectedQuoteForPurchase, setSelectedQuoteForPurchase] = useState<any>(null);
  const [showProfileCompletionDialog, setShowProfileCompletionDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [showBillingAddressDialog, setShowBillingAddressDialog] = useState(false);
  const [selectedQuoteForBilling, setSelectedQuoteForBilling] = useState<any>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Get user and credits
  const { user } = useSelector((state: any) => state.auth);
  const { total: userCredits, refetch: refetchCredits } = useUserCredits(user?.id);
  const { userBalance } = useSelector((state: any) => state.credits);
  const { settings: categoryFormSettings } = useSelector((state: any) => state.categoryFormSettings);
  const { isVerified, goToVerification, verificationData } = useVerification();
  
  // Track if verification data has been loaded at least once
  const [verificationDataLoaded, setVerificationDataLoaded] = useState(false);

  // Use quotes from Redux state - filtering is done on backend
  // Backend automatically filters out Assign quotes that have been purchased
  const quotes = userQuotes || [];
  
  // Debug logging
  console.log("[PurchasedQuoteListing] Quotes loaded:", {
    quotesCount: quotes.length,
    totalCount,
    hasNextPage,
    currentPage,
    pageSize,
    purchaseTypeIds
  });

  // Fetch category form settings on mount
  useEffect(() => {
    dispatch(fetchCategoryFormSettings());
  }, [dispatch]);

  // Track when verification data has loaded
  useEffect(() => {
    if (verificationData !== null && verificationData !== undefined) {
      setVerificationDataLoaded(true);
    }
  }, [verificationData]);

  // Requirement validation functions
  const checkProfileCompletion = () => {
    if (user && !user.phone) {
      setShowProfileCompletionDialog(true);
      return false;
    }
    return true;
  };

  const checkVerification = () => {
    // Don't check verification if data hasn't loaded yet
    if (!verificationDataLoaded) {
      return true; // Allow action to proceed while loading
    }
    
    if (user && !isVerified()) {
      setShowVerificationDialog(true);
      return false;
    }
    return true;
  };

  const checkRequirements = () => {
    // Don't block actions if verification data is still loading
    if (!verificationDataLoaded) {
      // Only check profile completion while verification loads
      if (!checkProfileCompletion()) {
        return false;
      }
      return true;
    }
    
    // Check verification first (higher priority)
    if (!checkVerification()) {
      return false;
    }
    // Then check profile completion
    if (!checkProfileCompletion()) {
      return false;
    }
    return true;
  };

  // Helper function to navigate to profile based on user role
  const navigateToProfile = () => {
    if (!user?.role?.id) return;

    const roleId = Number(user.role.id);
    if (roleId === RoleEnum.professional || roleId === RoleEnum.internationl_professional) {
      navigate("/myprofile");
    } else if (roleId === RoleEnum.client || roleId === RoleEnum.internationl_client) {
      navigate("/client/profile");
    } else if (roleId === RoleEnum.admin) {
      navigate("/admin/dashboard");
    }
    setShowProfileCompletionDialog(false);
  };

  // Handle billing saved callback
  const handleBillingSaved = () => {
    const billingCheck = checkUserBillingInfo(user);

    if (billingCheck.hasBillingInfo && selectedQuoteForBilling) {
      // Re-trigger purchase with updated billing info
      handlePurchase(selectedQuoteForBilling);
      setSelectedQuoteForBilling(null);
    }
  };

  // Function to mask private information for Assign type quotes
  const maskPrivateInfo = (info: any, type: string, quote?: any) => {
    // If quote is purchased (not Assign type), show unmasked information
    if (quote && Number(quote.purchaseType?.id) !== PurchaseQuoteTypeEnum.Assign) {
      return info || "N/A";
    }

    // Handle null, undefined, or empty strings
    if (!info || typeof info !== "string" || info.trim() === "") {
      return "N/A";
    }

    switch (type) {
      case "name":
        return info.replace(/(?!^.).(?!$)/g, "*");
      case "email": {
        if (!info.includes("@")) {
          return "N/A";
        }
        const [localPart, domain] = info.split("@");
        if (!localPart || !domain || !domain.includes(".")) {
          return "N/A";
        }
        const domainParts = domain.split(".");
        return `${localPart[0]}***@${domainParts[0][0]}***.${domainParts[1] || "com"}`;
      }
      case "phone":
        return info.replace(/\d(?=\d{4})/g, "*");
      case "address":
        return info.replace(/\d+|[a-zA-Z]+(?= )/g, "***");
      default:
        return info;
    }
  };

  // Function to get quote price based on settings
  const getQuotePrice = (isEmergency: boolean) => {
    if (!categoryFormSettings) return 20;

    let settings;
    if (Array.isArray(categoryFormSettings) && categoryFormSettings.length > 0) {
      settings = categoryFormSettings[0];
    } else if (typeof categoryFormSettings === "object" && !Array.isArray(categoryFormSettings)) {
      settings = categoryFormSettings;
    } else {
      return 20;
    }

    if (isEmergency) {
      return settings.emergencyQuotePrice || 20;
    } else {
      return settings.standardQuotePrice || 20;
    }
  };

  const fetchPurchasedQuotes = useCallback(async () => {
    if (!userId) return;

    try {
      const filters: any = {};
      
      // Use purchaseTypeIds if provided, otherwise fetch all types
      if (purchaseTypeIds) {
        filters.purchaseTypeIds = purchaseTypeIds;
      }

      const resultAction = await dispatch(fetchQuotesByUserIdWithFilters({
        userId: userId,
        filters: filters,
        sort: [{ orderBy: "createdAt", order: "desc" }],
        page: currentPage,
        limit: pageSize
      }));

      if (fetchQuotesByUserIdWithFilters.fulfilled.match(resultAction)) {
        // Quotes are now stored in Redux state
        console.log("[PurchasedQuoteListing] Quotes fetched successfully:", {
          payload: resultAction.payload,
          currentPage,
          pageSize,
          filters
        });
      } else {
        console.error("[PurchasedQuoteListing] Failed to fetch purchased quotes:", resultAction.payload);
      }
    } catch (err: any) {
      console.error("Error fetching purchased quotes:", err);
    }
  }, [dispatch, userId, currentPage, pageSize, purchaseTypeIds]);

  // Handle Contact button - Navigate to chat
  const handleContact = (quote: any) => {
    if (!quote.job?.userId?.id) {
      GlobalService.showToast("error", t("toast.titles.error"), t("purchasedQuoteListing.clientInfoNotAvailable"));
      return;
    }

    const clientId = quote.job.userId.id;
    const clientName = encodeURIComponent(
      `${quote.job.userId.firstName || ""} ${quote.job.userId.lastName || ""}`.trim() || "Client"
    );

    window.location.href = "/MessageAndVideoCall?professionalId=" + clientId + "&professionalName=" + encodeURIComponent(quote.job.userId.firstName + " " + quote.job.userId?.lastName);
    // navigate(`/MessageAndVideoCall?professionalId=${clientId}&professionalName=${encodeURIComponent(quote.job.userId.firstName + " " + quote.job.userId?.lastName)}`);
  };

  // Handle Purchase button - Open purchase modal
  const handlePurchase = (quote: any) => {
    if (!quote.job) {
      GlobalService.showToast("error", t("toast.titles.error"), t("purchasedQuoteListing.jobInfoNotAvailable"));
      return;
    }

    // Check if user needs to complete profile and verification
    if (!checkRequirements()) {
      return;
    }

    // Check if user has billing information
    const billingCheck = checkUserBillingInfo(user);

    if (!billingCheck.hasBillingInfo) {
      // Store the quote for auto-opening purchase modal after billing setup
      setSelectedQuoteForBilling(quote);
      // Show billing address requirement dialog
      setShowBillingAddressDialog(true);
      return;
    }

    const isEmergency = quote.job.isEmergency || Number(quote.job.deadlineType?.id) === 5;
    const quotePrice = getQuotePrice(isEmergency);

    setSelectedQuoteForPurchase({
      id: quote.job.id,
      amount: quotePrice,
      job: {
        id: quote.job.id,
        title: quote.job.title || "Emergency Job"
      },
      professional: {
        id: userId,
        firstName: user?.firstName,
        lastName: user?.lastName
      },
      isEmergency: isEmergency,
      useEmergencyCredits: isEmergency
    });
    setShowQuotePurchaseModal(true);
  };

  useEffect(() => {
    fetchPurchasedQuotes();
  }, [fetchPurchasedQuotes]);

  // Reset to page 1 when purchaseTypeIds changes
  useEffect(() => {
    setCurrentPage(1);
  }, [purchaseTypeIds]);

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle purchase success
  const handlePurchaseSuccess = async (purchasedQuote: any) => {
    console.log("Quote purchased successfully:", purchasedQuote);
    setShowQuotePurchaseModal(false);
    GlobalService.showToast("success", t("toast.titles.success"), t("purchasedQuoteListing.quotePurchaseSuccess"));

    // Refetch quotes to update the list
    await fetchPurchasedQuotes();

    // Navigate to chat with client
    if (purchasedQuote.job?.userId?.id) {
      const clientId = purchasedQuote.job.userId.id;
      const clientName = encodeURIComponent(
        `${purchasedQuote.job.userId.firstName || ""} ${purchasedQuote.job.userId.lastName || ""}`.trim() || "Client"
      );
      navigate(`/MessageAndVideoCall?professionalId=${userId}&professionalName=${encodeURIComponent(user?.firstName + " " + user?.lastName)}&userId=${clientId}&from=myproposals`);
    }
  };

  // Helper functions
  const getStatusInfo = (status) => {
    if (typeof status === "object" && status?.id) {
      const statusMap = {
        [QuoteStatusEnum.Pending]: { text: t("purchasedQuoteListing.statusBadges.pending"), style: "bg-yellow-100 text-yellow-800" },
        [QuoteStatusEnum.Approved]: { text: t("purchasedQuoteListing.statusBadges.approved"), style: "bg-blue-100 text-blue-800" },
        [QuoteStatusEnum.Rejected]: { text: t("purchasedQuoteListing.statusBadges.rejected"), style: "bg-red-100 text-red-800" },
        [QuoteStatusEnum.Used]: { text: t("purchasedQuoteListing.statusBadges.used"), style: "bg-green-100 text-green-800" },
        [QuoteStatusEnum.Completed]: { text: t("purchasedQuoteListing.statusBadges.completed"), style: "bg-green-100 text-green-800" },
        [QuoteStatusEnum.Active]: { text: t("purchasedQuoteListing.statusBadges.active"), style: "bg-purple-100 text-purple-800" },
        [QuoteStatusEnum.InActive]: { text: t("purchasedQuoteListing.statusBadges.inactive"), style: "bg-gray-100 text-gray-800" }
      };
      return statusMap[status.id] || { text: t("purchasedQuoteListing.statusBadges.unknown"), style: "bg-gray-100 text-gray-800" };
    }
    return { text: t("purchasedQuoteListing.statusBadges.unknown"), style: "bg-gray-100 text-gray-800" };
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusInfo = getStatusInfo(status);

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.style}`}>
        {statusInfo.text}
      </span>
    );
  };

  const getPurchaseTypeText = (purchaseType: any) => {
    if (typeof purchaseType === "object" && purchaseType?.id) {
      const typeMap = {
        [PurchaseQuoteTypeEnum.Purchase]: t("purchasedQuoteListing.purchaseType.purchased"),
        [PurchaseQuoteTypeEnum.Credit]: t("purchasedQuoteListing.purchaseType.credits"),
        [PurchaseQuoteTypeEnum.Assign]: t("purchasedQuoteListing.purchaseType.assigned"),
        [PurchaseQuoteTypeEnum.Promotional]: t("purchasedQuoteListing.purchaseType.promotional")
      };
      return typeMap[purchaseType.id] || t("quoteDetailModal.unknown");
    }
    return t("quoteDetailModal.unknown");
  };

  const getPurchaseTypeIcon = (purchaseType: any) => {
    if (typeof purchaseType === "object" && purchaseType?.id) {
      const iconMap = {
        [PurchaseQuoteTypeEnum.Purchase]: DollarSign,
        [PurchaseQuoteTypeEnum.Credit]: CreditCard,
        [PurchaseQuoteTypeEnum.Assign]: UserCheck,
        [PurchaseQuoteTypeEnum.Promotional]: Gift
      };
      return iconMap[purchaseType.id] || DollarSign;
    }
    return DollarSign;
  };

  return (
    <div className="divide-y">
      {loading ? (
        <div className="p-6 text-center">
          <div className="flex items-center justify-center">
            <Clock className="h-5 w-5 animate-spin mr-2" />
            {t("purchasedQuoteListing.loading")}
          </div>
        </div>
      ) : error ? (
        <div className="p-6 text-center text-red-500">
          <AlertCircle className="h-8 w-8 mx-auto mb-2" />
          <p className="mb-4">{error}</p>
          <Button onClick={fetchPurchasedQuotes}>
            {t("purchasedQuoteListing.tryAgain")}
          </Button>
        </div>
      ) : quotes.length === 0 ? (
        <div className="p-6 text-center text-gray-500">
          <Receipt className="h-8 w-8 mx-auto mb-2" />
          <p>{t("purchasedQuoteListing.noPurchasedQuotes")}</p>
          <p className="text-sm">{t("purchasedQuoteListing.noPurchasedQuotesDesc")}</p>
        </div>
      ) : (
        quotes.map((quote: any) => {
          const statusInfo = getStatusInfo(quote.status);
          const PurchaseIcon = getPurchaseTypeIcon(quote.purchaseType);
          const purchaseTypeText = getPurchaseTypeText(quote.purchaseType);

          return (
            <div
              key={quote.id}
              className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex flex-col sm:flex-row justify-between gap-4" onClick={() => setSelectedQuote(quote)}>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium text-gray-900">
                      {quote.job?.title || t("purchasedQuoteListing.quotePurchase")}
                    </h3>
                    <StatusBadge status={quote.status} />
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${Number(quote.purchaseType?.id) === PurchaseQuoteTypeEnum.Promotional ? "border-purple-200 bg-purple-50 text-purple-700" : ""}`}
                    >
                      <PurchaseIcon className="h-3 w-3 mr-1" />
                      {purchaseTypeText}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {t("purchasedQuoteListing.quoteId")}: {quote.id} • {t("purchasedQuoteListing.jobId")}: {quote.job?.id || "N/A"}
                  </p>

                  {/* Client Information */}
                  {quote.job?.userId && (
                    <div className="flex items-center gap-4 mt-2">
                      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                        {(() => {
                          const client = quote.job.userId;
                          const photoUrl = client?.photo?.path || client?.photo?.url || client?.profilePhoto || "";
                          const name = (client?.firstName && client?.lastName) ? `${client.firstName} ${client.lastName}` : (client?.firstName || client?.name || "Client");
                          return photoUrl ? (
                            <img src={photoUrl} alt={name} className="h-10 w-10 rounded-full object-cover" />
                          ) : (
                            <User className="h-5 w-5 text-gray-600" />
                          );
                        })()}
                      </div>
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {maskPrivateInfo(
                              `${quote.job.userId?.firstName || ""} ${quote.job.userId?.lastName || ""}`.trim() || "Client",
                              "name",
                              quote
                            )}
                          </span>
                          {quote.job.userId?.verified && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">{t("purchasedQuoteListing.verified")}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 text-yellow-400 fill-current" />
                            <span>{quote.job.userId?.averageRating || 0}</span>
                          </div>
                          {quote.job.userId?.reviewsCount && (
                            <span>({quote.job.userId.reviewsCount} {t("purchasedQuoteListing.reviews")})</span>
                          )}
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            <span>{maskPrivateInfo(quote.job.userId?.location || quote.job.userId?.address || t("purchasedQuoteListing.locationNotSpecified"), "address", quote)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-sm text-gray-600 mt-2">
                    {quote.job?.description || t("purchasedQuoteListing.noDescriptionAvailable")}
                  </p>
                </div>

                <div className="flex flex-col sm:items-end gap-2">
                  <div className="text-right">
                    <p className={`text-lg font-medium ${Number(quote.purchaseType?.id) === PurchaseQuoteTypeEnum.Promotional ? "text-purple-600" : "text-gray-900"}`}>
                      {Number(quote.purchaseType?.id) === PurchaseQuoteTypeEnum.Promotional 
                        ? t("purchasedQuoteListing.free")
                        : `${getDynamicCurrencySymbol()}${quote.amount?.toFixed(2) || "0.00"}`
                      }
                    </p>
                    <p className="text-sm text-gray-600">
                      {Number(quote.purchaseType?.id) === PurchaseQuoteTypeEnum.Assign 
                        ? t("purchasedQuoteListing.assigned")
                        : Number(quote.purchaseType?.id) === PurchaseQuoteTypeEnum.Promotional
                          ? t("purchasedQuoteListing.claimed")
                          : t("purchasedQuoteListing.purchased")}: {new Date(quote.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Show buttons - Assign type has Contact, Purchase, and See More */}
                  {Number(quote.purchaseType?.id) === PurchaseQuoteTypeEnum.Assign ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContact(quote);
                          }}
                          className="flex items-center gap-2"
                        >
                          <MessageCircle className="h-4 w-4" />
                          {t("purchasedQuoteListing.contact")}
                        </Button>
                        {quote.job.isQuote && <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePurchase(quote);
                          }}
                          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        >
                          <ShoppingCart className="h-4 w-4" />
                          {t("purchasedQuoteListing.purchase")}
                        </Button>
                        }
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedQuote(quote);
                          setShowQuoteDetails(true);
                        }}
                        className="flex items-center gap-2 w-full"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {t("purchasedQuoteListing.seeMore")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedQuote(quote);
                        setShowQuoteDetails(true);
                      }}
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      {t("purchasedQuoteListing.seeMore")}
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MessageCircle className="h-4 w-4" />
                    <span>0 {t("purchasedQuoteListing.messages")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="h-4 w-4" />
                    <span>0 {t("purchasedQuoteListing.files")}</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <Star className="h-4 w-4 text-yellow-400 fill-current" />
                    <span>{quote.job?.userId?.averageRating || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* Pagination */}
      {!loading && quotes.length > 0 && totalCount > 0 && (
        <div className="px-4 py-4 border-t flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">
              {t("purchasedQuoteListing.showingQuotes", { count: quotes.length, total: totalCount, page: currentPage, totalPages: Math.ceil(totalCount / pageSize) })}
            </span>
          </div>
          {(totalCount > pageSize || hasNextPage || currentPage > 1) && (
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {/* Previous/Next Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("purchasedQuoteListing.previous")}
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!hasNextPage && currentPage >= Math.ceil(totalCount / pageSize)}
                  className="px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("purchasedQuoteListing.next")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quote Details Modal */}
      <QuoteDetailModal
        isOpen={showQuoteDetails}
        onClose={() => setShowQuoteDetails(false)}
        quote={selectedQuote}
      />

      {/* Quote Purchase Modal */}
      <QuotePurchaseModal
        isOpen={showQuotePurchaseModal}
        onClose={() => setShowQuotePurchaseModal(false)}
        onSuccess={handlePurchaseSuccess}
        quote={selectedQuoteForPurchase}
        userCredits={userCredits}
        refetchCredits={refetchCredits}
        useEmergencyCredits={selectedQuoteForPurchase?.useEmergencyCredits}
        emergencyAvailableCredits={userBalance?.emergencyAvailableCredits}
        emergencyUsedCredits={userBalance?.emergencyUsedCredits}
      />

      {/* Profile Completion Dialog */}
      <ProfileCompletionDialog
        isOpen={showProfileCompletionDialog}
        onClose={() => setShowProfileCompletionDialog(false)}
        onGoToProfile={navigateToProfile}
        userRole={user?.role?.id}
      />

      {/* Verification Requirement Dialog */}
      <VerificationRequirementDialog
        isOpen={showVerificationDialog}
        onClose={() => setShowVerificationDialog(false)}
        title="Account Verification Required"
        message="Please complete your account verification to purchase this quote."
        showProgress={true}
        showMissingSteps={true}
      />

      {/* Billing Address Requirement Dialog */}
      <BillingAddressRequirementDialog
        isOpen={showBillingAddressDialog}
        onClose={() => {
          setShowBillingAddressDialog(false);
          setSelectedQuoteForBilling(null);
        }}
        onBillingSaved={handleBillingSaved}
        userRole={Number(user?.role?.id)}
        action="payment"
      />
    </div>
  );
};

export default PurchasedQuoteListing;